import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { getSeatReport } from "../../server/Api";
import { getOccasionThemeByIndex } from "../../utils/allocateQrThemes";
import {
  ReportPageShell,
  exportRowsToExcel,
  safeFileLabel,
} from "./reportShared";
import "../../assets/css/occasion.css";
import "../../assets/css/StayReports.css";
import "../../assets/css/FestivaReports.css";

const seatLabel = (row) => {
  if (row.allocationType === "row" || row.rowNumber !== "") {
    if (row.rowNumber !== "" || row.rowSeat !== "") {
      return `Row ${row.rowNumber || "—"} · Seat ${row.rowSeat || "—"}`;
    }
  }
  if (row.tableNumber !== "" || row.tableSeat !== "") {
    return `Table ${row.tableNumber || "—"} · Seat ${row.tableSeat || "—"}`;
  }
  return row.allocationType || "—";
};

const occasionThemeVars = (theme) => ({
  "--seat-occ-primary": theme.primary,
  "--seat-occ-soft": theme.soft,
  "--seat-occ-page": theme.page,
  "--seat-occ-chip": theme.chip,
  "--seat-occ-bar": theme.bar,
});

const countPlacement = (list = []) => {
  let inRows = 0;
  let atTables = 0;
  list.forEach((row) => {
    const type = String(row.allocationType || "")
      .trim()
      .toLowerCase();
    const hasRow = row.rowNumber !== "" && row.rowNumber != null;
    const hasTable = row.tableNumber !== "" && row.tableNumber != null;
    if (type === "row" || (type !== "table" && hasRow)) inRows += 1;
    if (type === "table" || (type !== "row" && hasTable)) atTables += 1;
  });
  return { inRows, atTables };
};

const SeatReport = () => {
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [occasionFilter, setOccasionFilter] = useState("");

  const loadReport = useCallback(async () => {
    if (!selectedEvent?.eventId) {
      setReport(null);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(getSeatReport(selectedEvent.eventId));
      setReport(res.data?.data || null);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load seat report"
      );
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent?.eventId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const byOccasion = report?.byOccasion || [];

  const occasionThemeById = useMemo(() => {
    const map = new Map();
    byOccasion.forEach((occ, index) => {
      map.set(String(occ.occasionId), getOccasionThemeByIndex(index));
    });
    return map;
  }, [byOccasion]);

  const visibleOccasions = useMemo(() => {
    if (!occasionFilter) return byOccasion;
    return byOccasion.filter(
      (occ) => String(occ.occasionId) === String(occasionFilter)
    );
  }, [byOccasion, occasionFilter]);

  const rows = useMemo(() => {
    let list = report?.rows || [];
    if (occasionFilter) {
      list = list.filter(
        (row) => String(row.occasionId) === String(occasionFilter)
      );
    }
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter((row) =>
      [
        row.name,
        row.guestName,
        row.occasionName,
        row.tableNumber,
        row.rowNumber,
        row.allocationType,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [report, searchTerm, occasionFilter]);

  const rowsByOccasion = useMemo(
    () =>
      visibleOccasions.map((occ) => ({
        occasion: occ,
        theme:
          occasionThemeById.get(String(occ.occasionId)) ||
          getOccasionThemeByIndex(0),
        rows: rows.filter(
          (row) => String(row.occasionId) === String(occ.occasionId)
        ),
      })),
    [visibleOccasions, rows, occasionThemeById]
  );

  const handleExport = () => {
    if (!rows.length) {
      toast.error("No seat rows to export.");
      return;
    }
    const eventLabel = safeFileLabel(selectedEvent?.eventName, "Event");
    exportRowsToExcel({
      rows,
      sheetName: "Seat Allocation",
      fileName: `${eventLabel}_Seat_Allocation.xlsx`,
      reportTitle: "Seat Allocation Report",
      eventName: selectedEvent?.eventName || "",
      columns: [
        { label: "Person Type", value: (r) => r.personType || "", width: 12 },
        { label: "Guest / Attendee Name", value: (r) => r.name || "", width: 24 },
        {
          label: "Main Guest",
          value: (r) =>
            r.personType === "Attendee" ? r.guestName || "" : r.name || "",
          width: 22,
        },
        { label: "Occasion", value: (r) => r.occasionName || "", width: 22 },
        {
          label: "Seat Assigned",
          value: (r) => seatLabel(r),
          width: 22,
        },
        {
          label: "Seating Type",
          value: (r) => {
            const type = String(r.allocationType || "").toLowerCase();
            if (type === "row") return "Row";
            if (type === "table") return "Table";
            if (type === "both") return "Row & Table";
            return r.allocationType || "";
          },
          width: 12,
        },
        { label: "Row No.", value: (r) => r.rowNumber ?? "", width: 10 },
        { label: "Row Seat No.", value: (r) => r.rowSeat ?? "", width: 12 },
        { label: "Table No.", value: (r) => r.tableNumber ?? "", width: 10 },
        { label: "Table Seat No.", value: (r) => r.tableSeat ?? "", width: 12 },
        { label: "Remarks", value: (r) => r.remarks || "", width: 24 },
      ],
    });
    toast.success("Export successful!");
  };

  const metrics = useMemo(
    () =>
      byOccasion.map((occ, index) => {
        const theme = getOccasionThemeByIndex(index);
        const active = String(occasionFilter) === String(occ.occasionId);
        return {
          key: String(occ.occasionId),
          label: occ.occasionName,
          value: occ.totalSeats ?? 0,
          hint: `${occ.allocated ?? 0} allocated · ${
            occ.seatedInRows ?? 0
          } rows · ${occ.seatedAtTables ?? 0} tables`,
          className: `festiva-seat-occasion-card${active ? " is-active" : ""}`,
          style: occasionThemeVars(theme),
        };
      }),
    [byOccasion, occasionFilter]
  );

  return (
    <ReportPageShell
      title="Seat Allocation Report"
      hasEvent={Boolean(selectedEvent?.eventId)}
      loading={loading}
      onRefresh={loadReport}
      onExport={handleExport}
      exportDisabled={!report}
      metrics={metrics}
      toolbar={
        <div className="stay-report-toolbar festiva-seat-toolbar">
          <div className="festiva-seat-toolbar-row">
            <div className="festiva-report-chip-row is-inline">
              <button
                type="button"
                className={`stay-report-chip${
                  !occasionFilter ? " is-active" : ""
                }`}
                onClick={() => setOccasionFilter("")}
              >
                All
              </button>
              {byOccasion.map((occ, index) => {
                const theme = getOccasionThemeByIndex(index);
                const active =
                  String(occasionFilter) === String(occ.occasionId);
                return (
                  <button
                    key={String(occ.occasionId)}
                    type="button"
                    className={`stay-report-chip festiva-seat-occasion-chip${
                      active ? " is-active" : ""
                    }`}
                    style={occasionThemeVars(theme)}
                    onClick={() =>
                      setOccasionFilter((prev) =>
                        String(prev) === String(occ.occasionId)
                          ? ""
                          : String(occ.occasionId)
                      )
                    }
                    title={`${occ.allocated ?? occ.count ?? 0} allocated of ${
                      occ.totalSeats ?? 0
                    } seats`}
                  >
                    {occ.occasionName}
                    <em>
                      {occ.allocated ?? occ.count ?? 0}
                      {occ.totalSeats ? ` / ${occ.totalSeats}` : ""}
                    </em>
                  </button>
                );
              })}
            </div>
            <input
              type="search"
              className="form-control stay-report-search festiva-seat-search"
              placeholder="Search name, table, occasion…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      }
    >
      {rowsByOccasion.map(({ occasion, theme, rows: occRows }) => {
        const placement = countPlacement(occRows);
        return (
        <section
          className="stay-report-section festiva-seat-occasion-section"
          key={String(occasion.occasionId)}
          style={occasionThemeVars(theme)}
        >
          <div className="stay-report-table-wrap festiva-seat-occasion-table-wrap">
            <div className="stay-report-table-head festiva-seat-occasion-table-head">
              <h3>{occasion.occasionName}</h3>
              <span>
                {occRows.length} seated · {placement.inRows} rows ·{" "}
                {placement.atTables} tables
              </span>
            </div>
            <div className="stay-report-table-scroll">
              <table className="table stay-report-table">
                <thead>
                  <tr>
                    <th>Sr</th>
                    <th>Name</th>
                    <th>Seat</th>
                    <th>Type</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {occRows.length ? (
                    occRows.map((row, index) => {
                      const isAttendee = row.personType === "Attendee";
                      return (
                        <tr
                          key={`${row.occasionId}-${row.guestId || ""}-${
                            row.attendeeId || "guest"
                          }-${index}`}
                          className={
                            isAttendee ? "festiva-seat-attendee-row" : ""
                          }
                        >
                          <td>{index + 1}</td>
                          <td>
                            <div
                              className={
                                isAttendee
                                  ? "festiva-seat-name is-attendee"
                                  : "festiva-seat-name is-guest"
                              }
                            >
                              <strong>{row.name}</strong>
                              <div className="festiva-report-muted">
                                {isAttendee
                                  ? `Attendee · ${row.guestName || "Main guest"}`
                                  : "Guest"}
                              </div>
                            </div>
                          </td>
                          <td>{seatLabel(row)}</td>
                          <td>{row.allocationType || "—"}</td>
                          <td>{row.remarks || "—"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No seating allocations for this occasion.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        );
      })}
    </ReportPageShell>
  );
};

export default SeatReport;
